import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { apiErr } from "@/lib/api";
import { fdt } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/pawkt/PageHeader";
import { Loading, Empty } from "@/components/pawkt/Empty";
import { Scissors, Link2Off, Phone, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const VIEWS = [["today", "Today"], ["upcoming", "Upcoming"], ["in_progress", "In progress"], ["completed", "Completed"], ["unable", "Unable to complete"]];
const LABEL = { not_started: ["Not started", "bg-slate-100 text-slate-700"], in_progress: ["In progress", "bg-sky-100 text-sky-800"],
  completed: ["Completed", "bg-emerald-100 text-emerald-800"], unable: ["Unable to complete", "bg-rose-100 text-rose-700"], cancelled: ["Cancelled", "bg-slate-100 text-slate-500"] };

function JobCard({ j }) {
  const qc = useQueryClient();
  const [reason, setReason] = useState("");
  const [askReason, setAskReason] = useState(false);
  const act = useMutation({
    mutationFn: (p) => api.post(`/grooming/tickets/${j.id}/simple`, p),
    onSuccess: (_, p) => { qc.invalidateQueries({ queryKey: ["groomer-work"] }); setAskReason(false); toast.success(p.action === "start" ? "Job started" : p.action === "complete" ? "Job completed — reception notified" : "Marked unable to complete — reception notified"); },
    onError: (e) => toast.error(apiErr(e)),
  });
  const [label, tone] = LABEL[j.simple_status] || LABEL.not_started;
  const h = j.handling || {};
  const flags = [h.aggression && "Aggressive", h.bite_risk && "Bite risk", h.fear_anxiety && "Anxious"].filter(Boolean);
  const open = j.simple_status === "not_started" || j.simple_status === "in_progress";
  return (
    <div className="rounded-2xl border border-pawkt-border bg-white p-4" data-testid={`groom-job-${j.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-sm font-bold text-slate-900">{fdt(j.start_time)} · {j.pet_name}<span className="font-normal text-slate-500"> · {j.species}{j.breed ? ` / ${j.breed}` : ""}</span></p>
          <p className="text-sm text-slate-700">{j.service_name}{j.addons?.length ? ` + ${j.addons.join(", ")}` : ""}</p>
          <p className="mt-0.5 flex items-center gap-1 text-xs text-slate-500"><Phone className="h-3 w-3" />{j.customer_name}{j.customer_mobile ? ` · ${j.customer_mobile}` : ""}</p>
        </div>
        <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${tone}`} data-testid={`groom-job-status-${j.id}`}>{label}</span>
      </div>
      {(flags.length > 0 || h.special_handling || h.skin_sensitivity || h.allergies || h.preferred_haircut || j.instructions) && (
        <div className="mt-2 space-y-0.5 rounded-lg bg-amber-50 p-2 text-xs text-amber-900" data-testid={`groom-job-handling-${j.id}`}>
          {flags.length > 0 && <p className="flex items-center gap-1 font-semibold"><AlertTriangle className="h-3 w-3" />{flags.join(" · ")}</p>}
          {j.instructions && <p>Instructions: {j.instructions}</p>}
          {h.preferred_haircut && <p>Preferred cut: {h.preferred_haircut}</p>}
          {h.special_handling && <p>Handling: {h.special_handling}</p>}
          {h.skin_sensitivity && <p>Skin: {h.skin_sensitivity}</p>}
          {h.allergies && <p>Allergies (grooming): {h.allergies}</p>}
        </div>
      )}
      {j.history?.length > 0 && <p className="mt-2 text-xs text-slate-500">Last grooms: {j.history.map((x) => `${(x.completed_at || "").slice(0, 10)} ${x.service_name}`).join(" · ")}</p>}
      {j.unable_reason && <p className="mt-2 text-xs text-rose-700">Reason: {j.unable_reason}</p>}
      {open && (
        <div className="mt-3 flex flex-wrap gap-2">
          {j.simple_status === "not_started" && <button onClick={() => act.mutate({ action: "start" })} disabled={act.isPending} data-testid={`groom-start-${j.id}`} className="rounded-full bg-slate-900 px-4 py-2 text-xs font-semibold text-white">Start grooming</button>}
          {j.simple_status === "in_progress" && <button onClick={() => act.mutate({ action: "complete" })} disabled={act.isPending} data-testid={`groom-complete-${j.id}`} className="rounded-full bg-emerald-600 px-4 py-2 text-xs font-semibold text-white">Complete grooming</button>}
          <button onClick={() => setAskReason((v) => !v)} data-testid={`groom-unable-${j.id}`} className="rounded-full border border-rose-200 px-4 py-2 text-xs font-semibold text-rose-700">Unable to complete</button>
        </div>
      )}
      {askReason && open && (
        <div className="mt-2 flex flex-col gap-2 sm:flex-row">
          <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (required)" data-testid={`groom-unable-reason-${j.id}`} className="min-w-0 flex-1 rounded-lg border border-slate-200 px-3 py-2 text-sm" />
          <button onClick={() => act.mutate({ action: "unable", reason })} disabled={reason.trim().length < 3 || act.isPending} data-testid={`groom-unable-confirm-${j.id}`} className="rounded-full bg-rose-600 px-4 py-2 text-xs font-semibold text-white disabled:opacity-50">Confirm</button>
        </div>
      )}
      {askReason && open && reason.trim().length < 3 && (
        <p className="mt-1 text-xs text-slate-500" data-testid={`groom-unable-hint-${j.id}`}>Enter a reason of at least 3 characters.</p>
      )}
    </div>
  );
}

export default function GroomerHome() {
  const { user } = useAuth();
  const [view, setView] = useState("today");
  const { data, isLoading } = useQuery({ queryKey: ["groomer-work", view], refetchInterval: 60000, queryFn: async () => (await api.get("/grooming/my-work", { params: { view } })).data });
  const hello = `Hi${user?.name ? `, ${user.name.split(" ")[0]}` : ""}`;
  if (data && !data.linked) {
    return (<div><PageHeader title={hello} subtitle="Your grooming day" />
      <Empty icon={Link2Off} title="Your login isn't linked to a staff profile yet" subtitle="Ask the owner to link your account to your groomer profile in Staff Management so your jobs appear here." /></div>);
  }
  const c = data?.counts || {};
  return (
    <div className="space-y-4" data-testid="groomer-home">
      <PageHeader title={hello} subtitle="Your assigned grooming jobs" />
      <div className="flex gap-1.5 overflow-x-auto pb-1" data-testid="groomer-tabs">
        {VIEWS.map(([k, l]) => <button key={k} onClick={() => setView(k)} data-testid={`groomer-tab-${k}`}
          className={`shrink-0 rounded-full px-3 py-1.5 text-xs font-semibold ${view === k ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600"}`}>{l} ({c[k] ?? 0})</button>)}
      </div>
      {isLoading ? <Loading /> : !(data?.items || []).length ? <Empty icon={Scissors} title="No jobs here" subtitle="Grooming jobs assigned to you will appear here." /> : (
        <div className="space-y-3" data-testid={`groomer-list-${view}`}>{data.items.map((j) => <JobCard key={j.id} j={j} />)}</div>
      )}
    </div>
  );
}
